// FeedbackScreen.tsx — 개선·에러 리포트 칸반보드.
// 계산/검증 로직은 전부 src/lib/feedback.ts 에 있다 (레이어링 규칙).
import { LoadingState } from "@/components/States";
import { Field, Icon, Pill, Select, TextInput } from "@/components/ui";
import { useTimezone } from "@/contexts/TimezoneContext";
import * as api from "@/lib/api";
import { formatInTimeZone } from "@/lib/timezone";
import {
  type FeedbackComment,
  type FeedbackPost,
  type FeedbackPriority,
  type FeedbackStatus,
  type FeedbackType,
  type Me,
  MAX_IMAGES_PER_POST,
  PRIORITY_LABELS,
  STATUS_LABELS,
  STATUS_ORDER,
  TYPE_LABELS,
  canChangeStatus,
  canEditPost,
  groupByStatus,
  validateImageFile,
  validatePostForm,
} from "@/lib/feedback";
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

// ----- 작은 표시 컴포넌트들 ------------------------------------------------------

const TypeBadge = ({ type }: { type: FeedbackType }) => (
  <span
    style={{
      fontSize: 11,
      fontWeight: 700,
      padding: "2px 8px",
      borderRadius: 6,
      background: type === "bug" ? "rgba(240,68,82,0.12)" : "rgba(69,147,252,0.12)",
      color: type === "bug" ? "#f04452" : "var(--brand)",
    }}
  >
    {TYPE_LABELS[type]}
  </span>
);

const PriorityBadge = ({ priority }: { priority: FeedbackPriority }) => {
  const color =
    priority === "high" ? "#f04452" : priority === "medium" ? "#e8a13a" : "var(--text-dim)";
  return <span style={{ fontSize: 11, fontWeight: 600, color }}>{PRIORITY_LABELS[priority]}</span>;
};

// 인증 헤더가 필요해 <img src> 를 못 쓰므로 blob URL 로 로드한다.
const AuthImage = ({ imageKey }: { imageKey: string }) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let revoked: string | null = null;
    api
      .fetchFeedbackImageUrl(imageKey)
      .then((u) => {
        revoked = u;
        setUrl(u);
      })
      .catch(() => setUrl(null));
    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [imageKey]);
  if (!url) return null;
  return (
    <img
      src={url}
      alt="첨부 이미지"
      style={{ maxWidth: "100%", borderRadius: 10, border: "1px solid var(--ring-input)" }}
    />
  );
};

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
  zIndex: 60,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const Modal = ({
  title,
  onClose,
  children,
  footer,
  width = 520,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) => (
  <div onClick={onClose} style={overlayStyle}>
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width,
        maxWidth: "92vw",
        maxHeight: "86vh",
        overflowY: "auto",
        background: "var(--bg-surface)",
        borderRadius: 12,
        boxShadow: "rgba(0,0,0,0.5) 0px 8px 24px",
        padding: 24,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: 18 }}>
        <div style={{ fontFamily: "var(--font-title)", fontWeight: 800, fontSize: 17 }}>
          {title}
        </div>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "transparent",
            border: 0,
            color: "var(--text-dim)",
            cursor: "pointer",
            display: "flex",
          }}
        >
          <Icon name="x" size={18} />
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>{children}</div>
      {footer && (
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 22 }}>
          {footer}
        </div>
      )}
    </div>
  </div>
);

// ----- 등록/수정 폼 모달 ---------------------------------------------------------

interface DraftImage {
  file: File;
  previewUrl: string;
}

const PostFormModal = ({
  initial,
  imagesEnabled,
  onClose,
  onSaved,
  onToast,
}: {
  initial: FeedbackPost | null; // null = 새 글
  imagesEnabled: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
  onToast: (msg: string) => void;
}) => {
  const [type, setType] = useState<FeedbackType>(initial?.type ?? "improvement");
  const [title, setTitle] = useState(initial?.title ?? "");
  const [body, setBody] = useState(initial?.body ?? "");
  const [priority, setPriority] = useState<FeedbackPriority>(initial?.priority ?? "medium");
  const [existingImages, setExistingImages] = useState<string[]>(initial?.images ?? []);
  const [draftImages, setDraftImages] = useState<DraftImage[]>([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const draftImagesRef = useRef<DraftImage[]>([]);
  draftImagesRef.current = draftImages;
  useEffect(
    () => () => {
      for (const d of draftImagesRef.current) URL.revokeObjectURL(d.previewUrl);
    },
    []
  );

  const totalImages = existingImages.length + draftImages.length;

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const next: DraftImage[] = [];
    for (const file of Array.from(files)) {
      const err = validateImageFile(file, totalImages + next.length);
      if (err) {
        onToast(err);
        break;
      }
      next.push({ file, previewUrl: URL.createObjectURL(file) });
    }
    if (next.length) setDraftImages((d) => [...d, ...next]);
  };

  const save = async () => {
    const err = validatePostForm({ type, title, body, priority });
    if (err) {
      onToast(err);
      return;
    }
    setSaving(true);
    try {
      const uploaded: string[] = [];
      for (const d of draftImages) uploaded.push(await api.uploadFeedbackImage(d.file));
      const images = [...existingImages, ...uploaded];
      if (initial) {
        await api.updateFeedbackPost(initial.id, { type, title, body, priority, images });
        onToast("게시물을 수정했습니다");
      } else {
        await api.createFeedbackPost({ type, title, body, priority, images });
        onToast("게시물을 등록했습니다");
      }
      await onSaved();
      onClose();
    } catch (e) {
      onToast(e instanceof Error ? e.message : "저장에 실패했습니다");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={initial ? "게시물 수정" : "개선 · 이슈 등록"}
      onClose={onClose}
      footer={
        <>
          <Pill variant="ghost" size="sm" onClick={onClose}>
            취소
          </Pill>
          <Pill variant="primary" size="sm" disabled={saving} onClick={() => void save()}>
            {saving ? "저장 중…" : "저장"}
          </Pill>
        </>
      }
    >
      <Field label="유형">
        <Select
          value={type}
          onChange={(v) => setType(v as FeedbackType)}
          options={[
            { value: "improvement", label: TYPE_LABELS.improvement },
            { value: "bug", label: TYPE_LABELS.bug },
          ]}
        />
      </Field>
      <Field label="우선순위">
        <Select
          value={priority}
          onChange={(v) => setPriority(v as FeedbackPriority)}
          options={[
            { value: "high", label: PRIORITY_LABELS.high },
            { value: "medium", label: PRIORITY_LABELS.medium },
            { value: "low", label: PRIORITY_LABELS.low },
          ]}
        />
      </Field>
      <Field label="제목">
        <TextInput value={title} onChange={setTitle} placeholder="제목을 입력해 주세요" />
      </Field>
      <Field label="내용">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={6}
          placeholder="개선 제안 또는 에러 상황을 적어 주세요"
          style={{
            width: "100%",
            boxSizing: "border-box",
            background: "var(--bg-base)",
            color: "var(--text-base)",
            border: "1px solid var(--ring-input)",
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 14,
            fontFamily: "inherit",
            resize: "vertical",
          }}
        />
      </Field>
      {imagesEnabled && (
        <Field label={`이미지 (${totalImages}/${MAX_IMAGES_PER_POST})`}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            {existingImages.map((key) => (
              <div key={key} style={{ position: "relative", width: 72, height: 72 }}>
                <div style={{ width: 72, height: 72, overflow: "hidden", borderRadius: 8 }}>
                  <AuthImage imageKey={key} />
                </div>
                <button
                  type="button"
                  onClick={() => setExistingImages((arr) => arr.filter((k) => k !== key))}
                  style={removeBtnStyle}
                >
                  <Icon name="x" size={12} />
                </button>
              </div>
            ))}
            {draftImages.map((d, i) => (
              <div key={d.previewUrl} style={{ position: "relative", width: 72, height: 72 }}>
                <img
                  src={d.previewUrl}
                  alt={`첨부 ${i + 1}`}
                  style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8 }}
                />
                <button
                  type="button"
                  onClick={() => {
                    URL.revokeObjectURL(d.previewUrl);
                    setDraftImages((arr) => arr.filter((x) => x !== d));
                  }}
                  style={removeBtnStyle}
                >
                  <Icon name="x" size={12} />
                </button>
              </div>
            ))}
            {totalImages < MAX_IMAGES_PER_POST && (
              <Pill variant="ghost" size="sm" onClick={() => fileRef.current?.click()}>
                <Icon name="plus" size={14} /> 추가
              </Pill>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </div>
        </Field>
      )}
    </Modal>
  );
};

const removeBtnStyle: CSSProperties = {
  position: "absolute",
  top: -6,
  right: -6,
  width: 20,
  height: 20,
  borderRadius: "50%",
  border: 0,
  background: "var(--bg-surface)",
  color: "var(--text-dim)",
  boxShadow: "rgba(0,0,0,0.4) 0 1px 4px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

// ----- 상세 모달 -----------------------------------------------------------------

const PostDetailModal = ({
  post,
  me,
  onClose,
  onEdit,
  onChanged,
  onToast,
}: {
  post: FeedbackPost;
  me: Me | null;
  onClose: () => void;
  onEdit: () => void;
  onChanged: () => Promise<void>;
  onToast: (msg: string) => void;
}) => {
  const { timezone } = useTimezone();
  const [comments, setComments] = useState<FeedbackComment[]>([]);
  const [commentText, setCommentText] = useState("");
  const [busy, setBusy] = useState(false);

  const loadComments = useCallback(async () => {
    try {
      setComments(await api.getFeedbackComments(post.id));
    } catch {
      // 댓글 로드 실패는 치명적이지 않다 — 빈 목록 유지
    }
  }, [post.id]);

  useEffect(() => {
    void loadComments();
  }, [loadComments]);

  const removePost = async () => {
    if (!window.confirm("이 게시물을 삭제할까요?")) return;
    setBusy(true);
    try {
      await api.deleteFeedbackPost(post.id);
      onToast("게시물을 삭제했습니다");
      await onChanged();
      onClose();
    } catch (e) {
      onToast(e instanceof Error ? e.message : "삭제에 실패했습니다");
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (status: FeedbackStatus) => {
    setBusy(true);
    try {
      await api.updateFeedbackStatus(post.id, status);
      onToast(`상태를 '${STATUS_LABELS[status]}'(으)로 변경했습니다`);
      await onChanged();
      onClose();
    } catch (e) {
      onToast(e instanceof Error ? e.message : "상태 변경에 실패했습니다");
    } finally {
      setBusy(false);
    }
  };

  const addComment = async () => {
    if (!commentText.trim()) return;
    setBusy(true);
    try {
      await api.createFeedbackComment(post.id, commentText.trim());
      setCommentText("");
      await loadComments();
    } catch (e) {
      onToast(e instanceof Error ? e.message : "댓글 등록에 실패했습니다");
    } finally {
      setBusy(false);
    }
  };

  const removeComment = async (cid: string) => {
    try {
      await api.deleteFeedbackComment(post.id, cid);
      await loadComments();
    } catch (e) {
      onToast(e instanceof Error ? e.message : "댓글 삭제에 실패했습니다");
    }
  };

  return (
    <Modal title={post.title} onClose={onClose} width={640}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <TypeBadge type={post.type} />
        <PriorityBadge priority={post.priority} />
        <span style={{ fontSize: 12, color: "var(--text-dim)" }}>
          {post.authorName} · {formatInTimeZone(post.createdAt, timezone)}
        </span>
        <div style={{ flex: 1 }} />
        {canChangeStatus(me) && (
          <Select
            value={post.status}
            onChange={(v) => void changeStatus(v as FeedbackStatus)}
            options={STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABELS[s] }))}
          />
        )}
      </div>
      <div style={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.7 }}>{post.body}</div>
      {post.images.map((key) => (
        <AuthImage key={key} imageKey={key} />
      ))}
      {canEditPost(post, me) && (
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Pill variant="ghost" size="sm" onClick={onEdit}>
            수정
          </Pill>
          <Pill variant="ghost" size="sm" disabled={busy} onClick={() => void removePost()}>
            <Icon name="trash" size={14} /> 삭제
          </Pill>
        </div>
      )}
      <div style={{ borderTop: "1px solid var(--ring-input)", paddingTop: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 10 }}>
          댓글 {comments.length}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {comments.map((c) => (
            <div key={c.id} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
              <b style={{ fontSize: 12.5, whiteSpace: "nowrap" }}>{c.authorName}</b>
              <span style={{ fontSize: 13, flex: 1, whiteSpace: "pre-wrap" }}>{c.body}</span>
              <span style={{ fontSize: 11, color: "var(--text-dim)", whiteSpace: "nowrap" }}>
                {formatInTimeZone(c.createdAt, timezone)}
              </span>
              {me?.uid === c.authorUid && (
                <button
                  type="button"
                  onClick={() => void removeComment(c.id)}
                  style={{
                    background: "transparent",
                    border: 0,
                    color: "var(--text-dim)",
                    cursor: "pointer",
                  }}
                >
                  <Icon name="x" size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <TextInput
              value={commentText}
              onChange={setCommentText}
              placeholder="댓글을 입력해 주세요"
            />
          </div>
          <Pill variant="primary" size="sm" disabled={busy} onClick={() => void addComment()}>
            등록
          </Pill>
        </div>
      </div>
    </Modal>
  );
};

// ----- 칸반 카드/컬럼 -------------------------------------------------------------

const KanbanCard = ({
  post,
  draggable,
  onOpen,
}: {
  post: FeedbackPost;
  draggable: boolean;
  onOpen: () => void;
}) => {
  const { timezone } = useTimezone();
  return (
    <div
      draggable={draggable}
      onDragStart={(e) => e.dataTransfer.setData("text/feedback-post-id", post.id)}
      onClick={onOpen}
      style={{
        background: "var(--bg-card)",
        borderRadius: 12,
        padding: "12px 14px",
        cursor: draggable ? "grab" : "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        border: "1px solid var(--bg-surface-3)",
      }}
    >
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <TypeBadge type={post.type} />
        <PriorityBadge priority={post.priority} />
      </div>
      <div style={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.4 }}>{post.title}</div>
      <div style={{ display: "flex", gap: 10, fontSize: 11.5, color: "var(--text-dim)" }}>
        <span>{post.authorName}</span>
        {post.images.length > 0 && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
            <Icon name="file" size={11} /> {post.images.length}
          </span>
        )}
        <span style={{ marginLeft: "auto" }}>{formatInTimeZone(post.createdAt, timezone)}</span>
      </div>
    </div>
  );
};

const KanbanColumn = ({
  status,
  posts,
  canDrop,
  onDropPost,
  onOpen,
}: {
  status: FeedbackStatus;
  posts: FeedbackPost[];
  canDrop: boolean;
  onDropPost: (postId: string, status: FeedbackStatus) => void;
  onOpen: (post: FeedbackPost) => void;
}) => {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        if (!canDrop) return;
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false);
        if (!canDrop) return;
        const id = e.dataTransfer.getData("text/feedback-post-id");
        if (id) onDropPost(id, status);
      }}
      style={{
        flex: 1,
        minWidth: 220,
        background: over ? "rgba(69,147,252,0.08)" : "var(--bg-shell)",
        borderRadius: 14,
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        transition: "background 120ms",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 4px" }}>
        <span style={{ fontWeight: 800, fontSize: 13.5 }}>{STATUS_LABELS[status]}</span>
        <span style={{ fontSize: 12, color: "var(--text-dim)" }}>{posts.length}</span>
      </div>
      {posts.map((p) => (
        <KanbanCard key={p.id} post={p} draggable={canDrop} onOpen={() => onOpen(p)} />
      ))}
    </div>
  );
};

// ----- 화면 ----------------------------------------------------------------------

export const FeedbackScreen = ({
  onToast,
  createRequest,
}: {
  onToast: (msg: string) => void;
  createRequest?: number;
}) => {
  const [posts, setPosts] = useState<FeedbackPost[]>([]);
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<FeedbackType | "all">("all");
  const [priorityFilter, setPriorityFilter] = useState<FeedbackPriority | "all">("all");
  const [selected, setSelected] = useState<FeedbackPost | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<FeedbackPost | null>(null);

  useEffect(() => {
    if (!createRequest) return;
    setEditing(null);
    setSelected(null);
    setFormOpen(true);
  }, [createRequest]);

  const reload = useCallback(async () => {
    const list = await api.getFeedbackPosts({
      ...(typeFilter !== "all" && { type: typeFilter }),
      ...(priorityFilter !== "all" && { priority: priorityFilter }),
    });
    setPosts(list);
  }, [typeFilter, priorityFilter]);

  useEffect(() => {
    setLoading(true);
    Promise.all([reload(), api.getMe().then(setMe)])
      .catch((e) => onToast(e instanceof Error ? e.message : "불러오기에 실패했습니다"))
      .finally(() => setLoading(false));
  }, [reload, onToast]);

  const dropPost = useCallback(
    async (postId: string, status: FeedbackStatus) => {
      const prev = posts;
      const target = prev.find((p) => p.id === postId);
      if (!target || target.status === status) return;
      // 낙관적 이동 — 실패 시 원복 + 토스트
      setPosts(prev.map((p) => (p.id === postId ? { ...p, status } : p)));
      try {
        await api.updateFeedbackStatus(postId, status);
        onToast(`'${target.title}' → ${STATUS_LABELS[status]}`);
      } catch (e) {
        setPosts(prev);
        onToast(e instanceof Error ? e.message : "상태 변경에 실패했습니다");
      }
    },
    [posts, onToast]
  );

  if (loading) return <LoadingState />;

  const grouped = groupByStatus(posts);

  return (
    <div style={{ padding: "22px 26px", display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Select
          value={typeFilter}
          onChange={(v) => setTypeFilter(v as FeedbackType | "all")}
          options={[
            { value: "all", label: "전체 유형" },
            { value: "improvement", label: TYPE_LABELS.improvement },
            { value: "bug", label: TYPE_LABELS.bug },
          ]}
        />
        <Select
          value={priorityFilter}
          onChange={(v) => setPriorityFilter(v as FeedbackPriority | "all")}
          options={[
            { value: "all", label: "전체 우선순위" },
            { value: "high", label: PRIORITY_LABELS.high },
            { value: "medium", label: PRIORITY_LABELS.medium },
            { value: "low", label: PRIORITY_LABELS.low },
          ]}
        />
      </div>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start", overflowX: "auto" }}>
        {STATUS_ORDER.map((status) => (
          <KanbanColumn
            key={status}
            status={status}
            posts={grouped[status]}
            canDrop={canChangeStatus(me)}
            onDropPost={(id, s) => void dropPost(id, s)}
            onOpen={setSelected}
          />
        ))}
      </div>
      {formOpen && (
        <PostFormModal
          initial={editing}
          imagesEnabled={me?.imagesEnabled ?? false}
          onClose={() => setFormOpen(false)}
          onSaved={reload}
          onToast={onToast}
        />
      )}
      {selected && !formOpen && (
        <PostDetailModal
          post={selected}
          me={me}
          onClose={() => setSelected(null)}
          onEdit={() => {
            setEditing(selected);
            setSelected(null);
            setFormOpen(true);
          }}
          onChanged={reload}
          onToast={onToast}
        />
      )}
    </div>
  );
};
